import React from "react";
import { AgGridReact } from "ag-grid-react";
import type { GridApi, ColDef } from "ag-grid-community";

import {
  TableHeader,
  Switch,
  IconFileUpload,
  IconFileDownload,
} from "@cdkglobal/radial";

import { StyledRdsAgGridWrapper } from "../../../../../utils/styled";
import { IconButtonWithTooltip } from "../../../../common/Tooltip";
import { CORRELATION_FILE_UPLOAD_ENABLED_KEYS } from "../../../constants";
import { RowData } from "../../../../../types";

interface Props {
  propertyKey: string;
  title: string;
  searchValue: string;
  displayedRowCount: number;
  rowsLength: number;
  isReadOnly: boolean;
  isNewEntryRecord: boolean;
  showErrorsOnly: boolean;
  errorRowCount: number;
  gridRowData: RowData[];
  columnDefs: ColDef[];
  gridRef: React.RefObject<AgGridReact>;
  defaultColDef: ColDef;
  setGridApi: (api: GridApi) => void;
  setDisplayedRowCount: (count: number) => void;
  handleSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setSearchValue: (value: string) => void;
  setShowErrorsOnly: (value: boolean) => void;
  handleImportRecords: () => void;
  handleExportRecords: () => void;
  tableHeight: string;
}

export default function RenderCorrelationAGGrid({
  propertyKey,
  title,
  searchValue,
  displayedRowCount,
  rowsLength,
  isReadOnly,
  isNewEntryRecord,
  showErrorsOnly,
  errorRowCount,
  gridRowData,
  columnDefs,
  gridRef,
  defaultColDef,
  setGridApi,
  setDisplayedRowCount,
  handleSearchInputChange,
  setSearchValue,
  setShowErrorsOnly,
  handleImportRecords,
  handleExportRecords,
  tableHeight,
}: Readonly<Props>): JSX.Element {
  const baseHeaderButtons = [
    <IconButtonWithTooltip
      key={`${propertyKey}-export-button`}
      icon={<IconFileDownload />}
      dataTestId={`${propertyKey}-export-button`}
      isDisabled={displayedRowCount === 0 || rowsLength === 0}
      onClick={handleExportRecords}
      text={`export-${propertyKey}`}
      tooltip={`Export ${title}`}
    />,

    <Switch
      key={`${propertyKey}-error-filter-switch`}
      checked={showErrorsOnly}
      label={
        errorRowCount > 0
          ? `Select all with errors (${errorRowCount})`
          : "Select all with errors"
      }
      name={`${propertyKey}-error-filter`}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        setShowErrorsOnly(event.target.checked)
      }
    />,
  ];

  const extraHeaderButtons = CORRELATION_FILE_UPLOAD_ENABLED_KEYS.includes(
    propertyKey,
  )
    ? [
        <IconButtonWithTooltip
          key={`${propertyKey}-file-upload-button`}
          icon={<IconFileUpload />}
          dataTestId={`${propertyKey}-file-upload-button`}
          isDisabled={isReadOnly || isNewEntryRecord}
          onClick={handleImportRecords}
          text="upload-correlation"
          tooltip={`Upload ${title}`}
        />,
        ...baseHeaderButtons,
      ]
    : baseHeaderButtons;
  return (
    <StyledRdsAgGridWrapper>
      <TableHeader
        id={`${propertyKey}-table-header`}
        dataTestId={`${propertyKey}-table-header`}
        hideBorder={false}
        headerTitle={title}
        hideTableTitle={false}
        isAlwaysExpanded={false}
        isDownloadable={false}
        isPrintable={false}
        isSearchable={true}
        searchInputValue={searchValue}
        searchInputWidth={"15rem"}
        searchInputName={`${propertyKey}-table-search-input`}
        onSearchInputChange={handleSearchInputChange}
        onSearchInputClear={() => setSearchValue("")}
        extraHeaderButtons={extraHeaderButtons}
      />

      <div
        className="ag-theme-balham"
        style={{
          width: "100%",
          maxHeight: tableHeight,
          height: tableHeight,
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={gridRowData}
          columnDefs={columnDefs}
          getRowId={(params) => `${propertyKey}-${params.data.rowIndex}`}
          rowHeight={70}
          quickFilterText={searchValue}
          suppressCellFocus
          onFirstDataRendered={(params) => {
            setGridApi(params.api);
            setDisplayedRowCount(params.api.getDisplayedRowCount());
          }}
          onFilterChanged={(params) => {
            setDisplayedRowCount(params.api.getDisplayedRowCount());
          }}
          defaultColDef={defaultColDef}
          className="ag-theme-balham"
        />
      </div>
    </StyledRdsAgGridWrapper>
  );
}
